/**
 * Button — the shared typed button primitive (UX-2, design-system universal kit).
 *
 * One radius (base, per the 2026-07-02 geometry ruling) + one primary
 * fill (sage-800 → forest-700 hover), both
 * sourced from the @theme tokens, closing the 5-radii / 3-primary-fills drift
 * the graded review flagged. Variants map onto the treatments the app already
 * established (modalFormStyles BTN_* + the X.6.x pill idiom) rather than
 * inventing new ones:
 *
 *   primary    bg-sage-800 text-cream, hover forest-700   (BTN_FOREST)
 *   secondary  bg-paper text-sage-800, sage-tint border   (quiet bordered)
 *   ghost      transparent, text-sage-800                 (BTN_GHOST)
 *   danger     bg-pepper text-cream                       (pepper = the flat-UI
 *              destructive accent; never raw red-*)
 *   pepper     bg-pepper text-white                       (the SPICE brand
 *              accent, for the one primary action on a SPICE face — never
 *              a destructive action, which stays `danger` even though it
 *              shares the fill: white text, not `danger`'s cream, holds
 *              AA on the pepper fill (4.69:1); cream falls just short)
 *
 * `type` defaults to "button" so a Button inside a <form> never submits
 * implicitly. All native button props (ref, aria-*, onClick, …) pass through;
 * React 19 treats ref as a regular prop, so no forwardRef wrapper is needed.
 * `loading` disables the button, sets aria-busy, and swaps the icon slot for
 * a spinner while keeping the label visible (no layout jump, accessible name
 * intact).
 *
 * tools/lint-raw-grey.sh bans hand-rolled <button className> in new files;
 * this file is the sanctioned render site.
 */

import { type ComponentPropsWithRef, type ReactNode } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'pepper'
  | 'primary-on-dark'
  | 'secondary-on-dark'
  | 'ghost-on-dark';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button, sets aria-busy, and shows a spinner in the icon slot. */
  loading?: boolean;
  /** Leading icon slot (e.g. a lucide icon); replaced by the spinner while loading. */
  icon?: ReactNode;
}

// Geometry ruling (Amanda 2026-07-02, UX-13 direction): base radius at
// the segmented toggle's density (4px/14px). The rendered box is compact;
// the interactive target is not: the before pseudo-element extends the
// hit area 4px above and below, holding the 36px touch floor.
const BASE =
  'relative inline-flex items-center justify-center gap-2 rounded-base font-semibold ' +
  "before:absolute before:inset-x-0 before:-inset-y-1 before:content-[''] " +
  // Disabled reads as a visible state (design review 2026-07-06): the solid
  // fill stays, dimmed, with a not-allowed cursor — never an invisible,
  // broken-looking button. The `disabled` attribute still blocks the click.
  // transition-colors, NOT transition-all: a button animating its own
  // font-size glides for a beat whenever the reader's text size changes
  // (the true-zoom fence caught it mid-transition at 13.04px).
  'transition-colors duration-[var(--dur-base)] disabled:opacity-50 ' +
  'disabled:cursor-not-allowed focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-sage-400';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-sage-800 text-cream shadow-sh1 hover:bg-forest-700 hover:shadow-sh2',
  secondary:
    'bg-paper text-sage-800 border border-sage-300 hover:border-sage-400 hover:bg-sage-800/[6%]',
  ghost: 'bg-transparent text-sage-800 hover:bg-sage-800/[6%] hover:text-forest-700',
  danger: 'bg-pepper text-cream shadow-sh1 hover:brightness-[0.96]',
  // The SPICE accent role: the one primary action on a SPICE face (a
  // SPICE-only client's composer, the SPICE side of the both-client
  // toggle, a SPICE-only therapist's Home). White, not danger's cream —
  // cream on this fill lands at 4.38:1 and misses AA; white clears 4.69:1.
  pepper: 'bg-pepper text-white shadow-sh1 hover:brightness-[0.96]',
  // The on-dark ladder for CTAs sitting on the DISPLAY (dark forest) band
  // (DL v1). primary-on-dark is the classic inversion — a solid cream fill
  // with forest text (never a gold fill: gold stays non-interactive accent in
  // bands). secondary-on-dark is the white-bordered ghost beneath it.
  'primary-on-dark':
    'bg-cream text-sage-800 shadow-sh1 hover:bg-paper hover:shadow-sh2',
  'secondary-on-dark':
    'bg-transparent text-cream border border-cream/40 hover:bg-cream/10 hover:border-cream/70',
  // The quiet rung of the on-dark ladder (UX sweep 2026-07-10): the ghost
  // treatment for affordances sitting ON the forest band, where the light
  // ghost's sage text and sage hover wash vanish against the dark ground.
  'ghost-on-dark': 'bg-transparent text-cream/85 hover:bg-cream/10 hover:text-cream',
};

// text + min-heights in REM (true-zoom ruling 2026-08-25: primitives
// scale with the reader's text size; min-h-7 is already rem).
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'min-h-7 px-[14px] py-1 text-[0.8125rem]',
  md: 'min-h-[1.875rem] px-[14px] py-1 text-sm',
};

/** The classes a Button of this variant and size draws, for a control
 *  that renders its own element in the house button's look (a download
 *  link-button). */
export function buttonClassName(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
  return [BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size]].join(' ');
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  type = 'button',
  disabled,
  className,
  children,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[buttonClassName(variant, size), className].filter(Boolean).join(' ')}
      {...rest}
    >
      {loading ? (
        <span
          data-testid="button-spinner"
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-pill border-2 border-current border-t-transparent"
        />
      ) : (
        icon
      )}
      {children}
    </button>
  );
}
